import { useState, useEffect, useCallback } from 'react';
import { ImageUploadField } from './ImageUploadField';
import { GameProspectProfileModal } from './GameProspectProfileModal';
import { GameTimelineModal } from './GameTimelineModal';
import { GameScenariosModal } from './GameScenariosModal';
import { GameFinalResultsModal } from './GameFinalResultsModal';
import { GameScoreMeterModal } from './GameScoreMeterModal';
import { getGameByCourseId } from '../../services/content/gameService';
import type { Game } from '../../types/content/game';

interface GameManagerModalProps {
  courseId: string;
  onClose: () => void;
}

export function GameManagerModal({ courseId, onClose }: GameManagerModalProps) {
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSubModal, setActiveSubModal] = useState<string | null>(null);

  const fetchGame = useCallback(() => {
    setLoading(true);
    getGameByCourseId(courseId)
      .then(setGame)
      .catch(() => setGame(null))
      .finally(() => setLoading(false));
  }, [courseId]);

  useEffect(() => { fetchGame(); }, [fetchGame]);

  const btnOutline = 'px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
          <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Game {game ? `— ${game.title || game.header || 'Untitled'}` : ''}
            </h2>

            {loading && <p className="text-sm text-gray-400">Loading...</p>}

            {!loading && !game && (
              <p className="text-sm text-gray-500">No game associated with this course.</p>
            )}

            {!loading && game && (
              <div className="space-y-5">
                {/* Header image */}
                <ImageUploadField
                  label="Header Image"
                  currentUrl={game.imageUrl}
                  uploadEndpoint={`/game/${game.id}/image`}
                  onUploaded={() => fetchGame()}
                />
                {game.imageUrl && (
                  <a href={game.imageUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all -mt-3">
                    {game.imageUrl}
                  </a>
                )}

                {/* Sub-editor buttons */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sections</label>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setActiveSubModal('prospect')} className={btnOutline}>
                      Prospect Profile
                      {game.prospectProfile?.name && <span className="text-xs text-gray-400 ml-1">({game.prospectProfile.name})</span>}
                    </button>
                    <button onClick={() => setActiveSubModal('timeline')} className={btnOutline}>
                      Timeline Stages
                      <span className="text-xs text-gray-400 ml-1">({game.timelineStages?.length ?? 0})</span>
                    </button>
                    <button onClick={() => setActiveSubModal('scenarios')} className={btnOutline}>
                      Scenarios
                      <span className="text-xs text-gray-400 ml-1">({game.scenarios?.length ?? 0})</span>
                    </button>
                    <button onClick={() => setActiveSubModal('results')} className={btnOutline}>
                      Final Results
                    </button>
                    <button onClick={() => setActiveSubModal('scoremeter')} className={btnOutline}>
                      Score Meter
                      {game.scoreMeter?.label && <span className="text-xs text-gray-400 ml-1">({game.scoreMeter.tiers?.length ?? 0} tiers)</span>}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Sub-modals render on top */}
      {activeSubModal === 'prospect' && game && (
        <GameProspectProfileModal game={game} onClose={() => setActiveSubModal(null)} onSuccess={() => { setActiveSubModal(null); fetchGame(); }} />
      )}
      {activeSubModal === 'timeline' && game && (
        <GameTimelineModal game={game} onClose={() => setActiveSubModal(null)} onSuccess={() => { setActiveSubModal(null); fetchGame(); }} />
      )}
      {activeSubModal === 'scenarios' && game && (
        <GameScenariosModal game={game} onClose={() => setActiveSubModal(null)} onSuccess={() => { setActiveSubModal(null); fetchGame(); }} />
      )}
      {activeSubModal === 'results' && game && (
        <GameFinalResultsModal game={game} onClose={() => setActiveSubModal(null)} onSuccess={() => { setActiveSubModal(null); fetchGame(); }} />
      )}
      {activeSubModal === 'scoremeter' && game && (
        <GameScoreMeterModal game={game} onClose={() => setActiveSubModal(null)} onSuccess={() => { setActiveSubModal(null); fetchGame(); }} />
      )}
    </>
  );
}
